import { useQuery } from "@tanstack/react-query";
import { Button, Select, Spin } from "antd";
import { queryCustomers } from "@/apis/tms/customer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "@/hooks/shared/debounce";
import { ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import classNames from "classnames";
import NewCustomerDialog from "../../customer/NewDialog";
import { DialogRef, TMSCustomerVo } from "@/types";

function useTMSCustomers(nameLike?: string) {
  return useQuery({
    queryKey: ["tms-customers", nameLike],
    queryFn: () => queryCustomers({ nameLike }),
  });
}

interface CustomerSelectProps {
  className?: string;
  selectClassName?: string;
  defaultValue?: string;
  disabled?: boolean;
  value?: string;
  onChange: (customer: TMSCustomerVo | undefined) => void;
}

export default function CustomerSelect(props: CustomerSelectProps) {
  const {
    className,
    selectClassName,
    defaultValue,
    disabled,
    value,
    onChange,
  } = props;
  const [nameLike, setNameLike] = useState(defaultValue ?? "");

  const debouncedNameLike = useDebounce(nameLike, 500);
  const { data: customers, isFetching } = useTMSCustomers(debouncedNameLike);

  const didAutoSelectRef = useRef(false);

  const newCustomerDialogRef = useRef<DialogRef>(null);

  const options = useMemo(() => {
    return customers?.items.map((customer) => ({
      label: customer.name,
      value: customer.name,
    }));
  }, [customers]);

  const unknownCustomer = useMemo(() => {
    if (isFetching || !options) return false;
    if (options.some((option) => option.value === value)) return false;
    return true;
  }, [isFetching, options, value]);

  const onCustomerCreated = useCallback(() => {
    newCustomerDialogRef.current?.showModal();
  }, []);

  const onCustomerSelected = useCallback(
    (customerName: string | undefined) => {
      const customer = customers?.items.find(
        (customer) => customer.name === customerName,
      );
      onChange(customer);
    },
    [customers, onChange],
  );

  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (!defaultValue) return;
    if (value) return;
    if (isFetching) return;

    const match = customers?.items?.find((c) => c.name === defaultValue);
    if (!match) {
      setNameLike("");
      return;
    }

    didAutoSelectRef.current = true;
    onChange(match);
  }, [defaultValue, value, isFetching, customers, onChange]);

  return (
    <div className={classNames(className, "flex flex-row items-center gap-2")}>
      <Select
        className={selectClassName}
        disabled={disabled}
        placeholder="Type to search customer name"
        value={value}
        showSearch
        filterOption={false}
        onSearch={(value) => setNameLike(value)}
        notFoundContent={
          isFetching ? <Spin size="small" /> : "No results found"
        }
        allowClear
        onChange={(value) => {
          didAutoSelectRef.current = true;
          setNameLike("");
          onCustomerSelected(value);
        }}
        options={options}
      />
      {unknownCustomer && (
        <div className="flex flex-row items-center gap-2">
          <div className="flex flex-row items-center gap-1 pl-2">
            <ExclamationCircleFilled className="!text-red-500" />
            <span className="text-red-500 italic text-[13px]">
              Unknown customer
            </span>
          </div>
        </div>
      )}
      <Button
        type="primary"
        onClick={() => {
          newCustomerDialogRef.current?.showModal();
        }}
        icon={<PlusOutlined />}
      >
        Create customer
      </Button>
      <NewCustomerDialog
        ref={newCustomerDialogRef}
        onCreated={onCustomerCreated}
      />
    </div>
  );
}
